import { PsHttpService, type PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type {
    fetchStepRequest,
    fetchStepResponse,
    RegenerateIdeaRequest,
    IdeaSolutionRequest,
    GenerateExtendIdeaRequest,
    FetchVideoResultRequest,
    ExportPptRequest,
    ExportPptPreviewRequest,
    ExportPptPreviewResponse,
    SolutionNoveltyAnalysisRes,
    SolutionValidationAnalysisRes,
    VideoSubmitRequest,
    VideoResultResponse,
    ImageSubmitRequest,
    ImageResultResponse,
} from '../../types';
import type { TrizApi } from '../triz-api';
import { FactoryPsHttpSSE } from '@patsnap-utils/sse';

@injectable()
export class TrizApiImpl extends PsHttpService implements TrizApi {
    @inject(FactoryPsHttpSSE)
    private _factorySsePsHttp!: FactoryPsHttpSSE;

    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/triz-mind`,
            headers: {
                'X-API-Version': '1.0',
            },
        };
    }

    fetchStepsData(data: fetchStepRequest) {
        return this.get<PatSnapCommonResponseData<fetchStepResponse>>({
            url: '/version',
            params: data,
        }).then((res) => {
            return res.data as fetchStepResponse;
        })
    }

    regenerateIdea(data: RegenerateIdeaRequest): Promise<any> {
        const baseURL = `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka-rd-agent/triz-mind`;
        return this.post<PatSnapCommonResponseData<any>>({
            url: '/idea/regenerate',
            data,
            baseURL,
        }).then((res) => res.data);
    }

    fetchIdeaSolutionValidate(data: IdeaSolutionRequest): Promise<SolutionValidationAnalysisRes> {
        return this.post<PatSnapCommonResponseData<SolutionValidationAnalysisRes>>({
            url: '/idea/solution_validate',
            data,
        }).then((res) => res.data as SolutionValidationAnalysisRes);
    }

    fetchIdeaSolutionNovelty(data: IdeaSolutionRequest): Promise<SolutionNoveltyAnalysisRes> {
        return this.post<PatSnapCommonResponseData<SolutionNoveltyAnalysisRes>>({
            url: '/idea/solution_novelty',
            data,
        }).then((res) => res.data as SolutionNoveltyAnalysisRes);
    }

    generateExtendIdea(data: GenerateExtendIdeaRequest): Promise<any> {
        return this.post<PatSnapCommonResponseData<any>>({
            url: '/idea/extend',
            data,
        }).then((res) => res.data);
    }

    submitVideo(data: VideoSubmitRequest): Promise<any> {
        const baseURL = `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka-rd-agent/triz-mind`;
        return this.post<PatSnapCommonResponseData<any>>({
            url: '/idea/video/submit',
            data,
            baseURL,
        }).then((res) => {
            return res.data;
        });
    }

    fetchVideoResult(data: FetchVideoResultRequest): Promise<VideoResultResponse> {
        const baseURL = `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka-rd-agent/triz-mind`;
        return this.get<PatSnapCommonResponseData<VideoResultResponse>>({
            url: '/idea/video/result',
            params: data,
            baseURL,
        }).then((res) => res.data as VideoResultResponse);
    }

    submitImage(data: ImageSubmitRequest): Promise<any> {
        const baseURL = `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka-rd-agent/triz-mind`;
        return this.post<PatSnapCommonResponseData<any>>({
            url: '/idea/image/submit',
            data,
            baseURL,
        }).then((res) => res.data);
    }

    fetchImageResult(data: { triz_mind_id: string; idea_id: string }): Promise<ImageResultResponse> {
        const baseURL = `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka-rd-agent/triz-mind`;
        return this.get<PatSnapCommonResponseData<ImageResultResponse>>({
            url: '/idea/image/result',
            params: data,
            baseURL,
        }).then((res) => res.data as ImageResultResponse);
    }

    exportPpt(data: ExportPptRequest): Promise<any> {
        const baseURL = `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka-rd-agent/triz-mind`;
        return this.post<PatSnapCommonResponseData<any>>({
            url: '/export/ppt',
            data,
            baseURL,
        }).then((res) => res.data);
    }

    fetchExportPptPreview(data: ExportPptPreviewRequest): Promise<ExportPptPreviewResponse> {
        const baseURL = `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka-rd-agent/triz-mind`;
        return this.get<PatSnapCommonResponseData<ExportPptPreviewResponse>>({
            url: '/export/ppt/preview',
            params: data,
            baseURL,
        }).then((res) => res.data as ExportPptPreviewResponse);
    }
}
